import React, { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";

import { Section, NotificationCard } from "@arp/react-ndbx";

//** API Hooks */
import { useGetQuote } from "../../../hooks/dataHooks/useGetQuoteData.js";
import { useGetCheckPaymentGatewayStatus } from "../../../hooks/dataHooks/useGetCheckPaymentGatewayStatus.js";
import { useGetSecureCoPaymentToken } from "../../../hooks/dataHooks/useGetSecureCoPaymentToken.js";
import { useGetPollingTokenDetails } from "../../../hooks/dataHooks/useGetPollingTokenDetails.js";
import { usePostCloseSecureCoConnection } from "../../../hooks/dataHooks/usePostCloseSecureCoConnection.js";
import useSharedData from "../../../hooks/useSharedData.ts";

//** Context Hooks */
import { PaymentContext } from "../../../context/BraintreeContext.jsx";

//----------------------------------------------------------------

const SecureCoModal = ({ showing, handleOnClose, recapture }) => {
	const [enableCloseApiCall, setEnableCloseApiCall] = useState(false);
	const [open, setOpen] = React.useState(false);

	//** Open and close the modal */
	const handleOpen = () => setOpen(true);

	const handleClose = () => {
		//* Set the form src = null
		const iFrameHandle = top.document.getElementById("securePaymentiFrame");
		iFrameHandle?.setAttribute("src", "about:blank");

		//* we need to close the SecureCo connection.
		setEnableCloseApiCall(true);

		//* Now close the form.
		setOpen(false);
		handleOnClose(false);
	};

	useEffect(() => {
		if (!showing) return;
		setOpen(true);
	}, [showing]);
	//** */

	const [frameAncestorsValue, setFrameAncestorsValue] = useState(
		window.location.origin
	);
	const [skipCVCValue, setSkipCVCValue] = useState(false);
	const paymentContext = useContext(PaymentContext);
	const {
		setCapturedData,
		setTransactionNonce,
		setFormValid,
		setRequestIdData,
	} = paymentContext;

	//** We use this for the stored JWT */
	const { isLoading: quoteIsLoading, data: quoteData } = useGetQuote();

	const { accessToken: sharedAccessToken, retrieveQuoteJourney } =
		useSharedData();

	useEffect(() => {
		setFormValid(false);
	}, []);

	//**! Ensuse this is set to false */
	const isDev = false;

	const tempAccessToken =
		"eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiJodHRwczovL2Rldi5lbGlmZWluc3VyZS5jb20uYXUvb2F1dGgyL3Rva2VuIiwiaXNzIjoidGVybWxpZmUtYXBpLmRldiIsInN1YiI6InRlcm1saWZlLWFwaS5kZXYuand0IiwiaWF0IjoxNzA5MDY4NTQzLCJleHAiOjE3MDkwOTczNDMsImp0aSI6ImRkODUwYmY0LTViYjYtNDlkYy05MjM5LTFjYTIxOTY3ZjU2OSIsInFpZCI6IjYzNFFDMzAwNTNBTFAiLCJlbHRpZCI6ImViMDBhYWExLWZjNWQtNDU3NS04OTczLWYwNjVhMzkzYjU5NCIsIm9rdGFVc2VySWQiOiIwMHUzc3FtODZiZ1B5QUtXYjNsNyIsImNjX3VzZXIiOnRydWUsInVzZXJuYW1lIjoiamNxYiIsImZpcnN0TmFtZSI6IkNvbGluIiwibGFzdE5hbWUiOiJCeXJuZSJ9.zL71UDOmxRCdRAvDKw8wxde5NXObsakwnTOpI9Xckvw";

	const jwtAccessToken = isDev ? tempAccessToken : sharedAccessToken;

	//** Check the status of the SecureCo API */
	const {
		data: checkGatewayStatusData,
		isLoading: checkIsLoading,
		isFetching: checkIsFetching,
		error: checkError,
	} = useGetCheckPaymentGatewayStatus({
		jwtToken: jwtAccessToken,
		enabled: !quoteIsLoading,
	});
	//**  */

	//** New Session  */
	// const [enableNewSession, setEnableNewSession] = useState(false);
	const [currentRefId, setCurrentRefId] = useState(null);
	//** */

	//** SecureCo Access Token */
	const {
		data: paymentTokenData,
		isLoading: paymentTokenIsLoading,
		isFetching: paymentTokenFetching,
		error: paymentTokenError,
		refetch,
	} = useGetSecureCoPaymentToken({
		jwtToken: jwtAccessToken,
		enabled: !checkIsLoading && !checkGatewayStatusData,
		priorRequestId: currentRefId,
	});

	const responseObj = {
		access_token: paymentTokenData?.access_token,
		token_type: paymentTokenData?.token_type,
		expires_in: paymentTokenData?.expires_in,
		requestId: paymentTokenData?.requestId,
		secureCoHPPUrl: paymentTokenData?.secureCoHPPUrl,
		secureCoMerchantId: paymentTokenData?.secureCoMerchantId,
	};

	const accessToken = responseObj?.access_token;
	const requestId = responseObj?.requestId;
	const secureCoMerchantId = responseObj?.secureCoMerchantId;
	const secureCoHPPUrl = responseObj?.secureCoHPPUrl;

	//* Get a new SecureCo session
	const startNewSession = () => {
		setCurrentRefId(requestId);
		// setEnableNewSession(true);
		refetch();
	};

	const [recapturData, setRecapturData] = useState(recapture);

	useEffect(() => {
		if (recapturData === false) return;

		setCurrentRefId(requestId); //* Set the current requestId to the previousId
		refetch(); //* Get a new requestId
	}, [recapturData]);

	//** */

	//** Poll the interaction: wait for pollingData.capture to be true */
	const {
		data: pollingData,
		isLoading: pollingIsLoading,
		isFetching: pollingIsFetching,
		isRefetchError,
		error: pollingError,
		refetch: refetchPolling,
	} = useGetPollingTokenDetails({
		jwtToken: jwtAccessToken,
		requestId: requestId,
		enabled: !paymentTokenIsLoading && open,
	});

	useEffect(() => {
		if (recapturData === false) return;
		if (currentRefId && currentRefId !== requestId) {
			setRecapturData(false);
			refetchPolling({
				jwtToken: jwtAccessToken,
				requestId: requestId,
				enabled: true,
			});
		}
	}, [recapturData, requestId, currentRefId]);

	useEffect(() => {
		//* When the polling "captured" is true,
		//* update the conext with the values.
		if (recapturData === true) return;

		if (!pollingData || pollingData?.captured === false) return;
		handleClose();
	}, [pollingData]);

	const {
		data: closegData,
		isLoading: closeIsLoading,
		isFetching: closeIsFetching,
		error: closeError,
	} = usePostCloseSecureCoConnection({
		jwtToken: jwtAccessToken,
		requestId: requestId,
		enabled: enableCloseApiCall,
	});

	useEffect(() => {
		if (enableCloseApiCall) {
			setRequestIdData(requestId); //* Set the request id.This needs to part of the bind payload
			setCapturedData(pollingData); //* Set the returned data in the context
			setTransactionNonce(pollingData?.nonce); //* Set the payment nonce.
			setFormValid(true);
		}
	}, [enableCloseApiCall, jwtAccessToken, requestId]);

	//----------------------------------------------------------------

	const style = {
		position: "relative",
		display: "flex",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: "90vw",
		height: "90vh",
		bgcolor: "background.paper",
		border: "2px solid #000",
		boxShadow: 24,
		p: 4,
	};

	const iframeStyle = {
		width: "100%",
		height: "100%",
		border: "none",
	};

	const generateFormContent = () => {
		return `
      <html>
        <body>
          <form id="hostedPaymentPageForm" method="post" action="${secureCoHPPUrl}">
            <input type="hidden" name="BearerToken" value="${accessToken}" />
            <input type="hidden" name="RequestID" value="${requestId}" />
            <input type="hidden" name="SecureCoAccountId" value="${secureCoMerchantId}" />
            <input type="hidden" name="frameAncestors" value="${frameAncestorsValue}" />
            <input type="hidden" name="TransactionType" value="createtoken" />
            <input type="hidden" name="RequestType" value="semafone" />
            <input type="hidden" name="frameSkipCVCAncestors" value="${skipCVCValue}" />
          </form>
          <script>
            document.getElementById('hostedPaymentPageForm').submit();
          </script>
        </body>
      </html>
    `;
	};

	return (
		<div>
			<Section mb={3} align="left">
				<Section sx={{ maxWidth: { xs: "250px" } }}>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpen}
						disabled={checkIsLoading && paymentTokenIsLoading}
					>
						Capture Card Details
					</Button>
				</Section>
				<Section mt={4} align="left">
					<>
						{checkIsLoading && paymentTokenIsLoading ? (
							<NotificationCard
								cardType="info"
								title={null}
								message="Checking Gateway Status"
							/>
						) : null}
					</>
				</Section>
				<Section mt={4} align="left">
					<>
						{checkGatewayStatusData === true ? (
							<NotificationCard
								cardType="warning"
								title={null}
								message="Please be advised that credit card payments may be unavailable due to a technical/system issue."
							/>
						) : null}
					</>
				</Section>
			</Section>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="secureCo-modal"
				aria-describedby="secureCo-modal-iFrame"
			>
				<Box sx={style}>
					{open && (
						<>
							<Stack
								display={"flex"}
								direction={"column"}
								sx={{ width: "100%", height: "100%" }} // Set height to 100%
								position={"relative"}
							>
								<Box height={"100%"} position={"relative"} bottom={0}>
									<iframe
										title="Secure Payment Form"
										srcDoc={generateFormContent()} // Set the iframe's content using srcDoc
										style={iframeStyle}
										id="securePaymentiFrame"
									></iframe>
								</Box>

								<Stack
									direction={"row"}
									gap={1}
									display={"flex"}
									justifyContent={"flex-end"} // Adjusted to evenly space the buttons
									position={"absolute"}
									bottom={0} // Positioned at the bottom
									width="95%" // Full width
									p={2} // Added padding for spacing
								>
									<Button
										variant="contained"
										sx={{ maxWidth: "fit-content" }}
										onClick={startNewSession}
										disabled={!!paymentTokenIsLoading}
									>
										New Session
									</Button>
									<Button
										variant="contained"
										sx={{ maxWidth: "fit-content" }}
										onClick={(e) => {
											handleClose(e);
										}} // Added onClick handler for the "Close" button
										disabled={!!paymentTokenIsLoading}
									>
										Close
									</Button>
								</Stack>
							</Stack>
						</>
					)}
				</Box>
			</Modal>
		</div>
	);
};

export default SecureCoModal;
